import request from '../utils/axios/request';
import Cookies from '../lib/cookies';
import { Client } from '../pages/fluxo-unico/types';

export interface IPermissoesUsuarioFluxoUnico {
	isReport: boolean;
	isMovement: boolean;
	isLocalAdmin: boolean;
	isMonitoring: boolean;
	isMaintenance: boolean;
	isSystemAdmin: boolean;
}

export interface IUsuarioFluxoUnico {
	id: number;
	name: string;
	phone: string | null;
	cpf: string | null;
	email: string;
	type: string;
	role: string;
	permissions: IPermissoesUsuarioFluxoUnico;
	client: Client;
	isActive: boolean;
	shouldReceiveAlerts: boolean;
	acessoFluxoUnico: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface ILoginFluxoUnico {
	access_token: string;
	user: IUsuarioFluxoUnico;
}

export interface IContainerFluxoUnico {
	id: number;
	label: string;
	temperature: number | null;
	return: number | null;
	supply: number | null;
	position: string | null;
	carrier: string | null;
	owner: string | null;
	ownerUser: string | null;
	isArrive: boolean;
	dateArrive: string | null;
	isLeave: boolean;
	dateLeave: string | null;
	movement: { yard: { id: number, name: string } };
	createdAt: string;
	updatedAt: string;
}

export interface IEmailDetalhesFluxoUnico {
	titulo: string | null;
	remetente: string | null;
	destinatarios: string | null;
}

export type TipoProblemaFluxoUnico =
	| 'critico'
	| 'severo'
	| 'alto'
	| 'medio'
	| 'baixo';

export type StatusOrdemServicoFluxoUnico =
	| 'em-atendimento'
	| 'rascunho'
	| 'fechada';

export interface IOrdemServicoFluxoUnico {
	id: number;
	idOrdemServicoRetificada?: number | null;
	id_ordem_servico_retificada?: number | null;
	usuario: IUsuarioFluxoUnico;
	container: IContainerFluxoUnico;
	monitoramento: IProblemaContainerFluxoUnico;
	data: string;
	setPoint: number;
	temperatura: number;
	imagens?: string[] | null;
	serial?: string | null;
	size?: string | null;
	model?: string | null;
	gas?: string | null;
	qty?: string | null;
	manufactoreDate?: string | null;
	manufactore_date?: string | null;
	damageDescription?: string | null;
	damage_description?: string | null;
	setPointAposServico?: number | null;
	temperaturaAposServico?: number | null;
	requestedAttendency?: string | null;
	requested_attendency?: string | null;
	requestDate?: string | null;
	request_date?: string | null;
	repairStart?: string | null;
	repair_start?: string | null;
	repairFinish?: string | null;
	repair_finish?: string | null;
	solicitadoPor?: string | null;
	dataSolicitacao?: string | null;
	inicioReparo?: string | null;
	fimReparo?: string | null;
	imagensAposServico?: string[] | null;
	descricaoServico?: string | null;
	local?: string | null;
	pecasTrocadas?: string | null;
	tempoExecucaoHoras?: number | null;
	tempoExecucaoMinutos?: number | null;
	observacaoServico?: string | null;
	nomeCompletoTecnico?: string | null;
	assinaturaTecnico?: string | null;
	nomeCompletoGestor?: string | null;
	assinaturaGestor?: string | null;
	tipoProblema: TipoProblemaFluxoUnico | number[];
	status: StatusOrdemServicoFluxoUnico;
	createdAt: string;
	updatedAt: string;
}

export interface IUpsertOrdemServicoFluxoUnico {
	id?: number;
	id_usuario?: number;
	id_container?: number;
	id_monitoramento?: number;
	id_ordem_servico_retificada?: number;
	data: string;
	set_point: number;
	temperatura: number;
	serial?: string;
	size?: string;
	model?: string;
	gas?: string;
	qty?: string;
	manufactore_date?: string;
	damage_description?: string;
	imagens?: string[];
	set_point_apos_servico?: number;
	temperatura_apos_servico?: number;
	requested_attendency?: string;
	request_date?: string;
	repair_start?: string;
	repair_finish?: string;
	imagens_apos_servico?: string[];
	descricao_servico?: string;
	local?: string;
	pecas_trocadas?: string;
	tempo_execucao_horas?: number;
	tempo_execucao_minutos?: number;
	observacao_servico?: string;
	nome_completo_tecnico?: string;
	assinatura_tecnico?: string;
	nome_completo_gestor?: string;
	assinatura_gestor?: string;
	tipo_problema?: TipoProblemaFluxoUnico | number[];
	status?: StatusOrdemServicoFluxoUnico;
	tipo_upsert?: 'rascunho' | 'fechar-enviar';
}

export interface IProblemaContainerFluxoUnico {
	id: number;
	temperature: number | null;
	return: number | null;
	supply: number | null;
	humidity: number | null;
	ventilation: number | null;
	observation: string | null;
	images: string[];
	problems: number[];
	images_legacy: string[] | null;
	problems_legacy: number[] | null;
	date: string;
	created_at: string;
	updated_at: string;
	container: IContainerFluxoUnico;
	email_detalhes?: IEmailDetalhesFluxoUnico | null;
	'ordens-servico'?: IOrdemServicoFluxoUnico[];
}

abstract class FluxoUnicoService {
	static async login(
		email: string,
		senha: string
	): Promise<ILoginFluxoUnico | undefined> {
		try {
			const resposta: ILoginFluxoUnico = await request(
				'/fluxo-unico/login',
				'post',
				{
					data: {
						email,
						password: senha,
					},
				},
				'guest'
			);

			Cookies.setAccessToken(resposta, true);
			return resposta;
		} catch (error: any) { }
	}

	static async getProblemaContainer(
		container: string
	): Promise<IProblemaContainerFluxoUnico | undefined> {
		try {
			const resposta: IProblemaContainerFluxoUnico = await request(
				'/fluxo-unico/container/problema',
				'get',
				{ params: { container } },
				'guest'
			);
			return resposta;
		} catch (error: any) { }
	}

	static async criarOrdemServico(
		dados: IUpsertOrdemServicoFluxoUnico
	): Promise<IOrdemServicoFluxoUnico | undefined> {
		try {
			const resposta: IOrdemServicoFluxoUnico = await request(
				'/fluxo-unico/ordens-servico',
				'post',
				{ data: dados }
			);

			return resposta;
		} catch (error: any) { }
	}
}

export default FluxoUnicoService;
